import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { ArticleSchema, FAQSchema, BreadcrumbSchema } from '../../components/seo/SchemaMarkup';
import { Breadcrumbs } from '../../components/seo/Breadcrumbs';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Calendar, Clock, User, ArrowRight } from 'lucide-react';
import { getBlogPostBySlug, blogPosts } from '../../data/blogPosts';
import Footer from '../../components/Footer';

// Import blog content components
import Top5NightClubs2026 from './Top5NightClubs2026';
import BestDJNightsWeeklyGuide from './BestDJNightsWeeklyGuide';
import UltimateGuideNightlife from './UltimateGuideNightlife';
import WhereToPartySaturday from './WhereToPartySaturday';
import ThingsToDoAtNight from './ThingsToDoAtNight';
import BachelorPartyJamshedpur from './BachelorPartyJamshedpur';
import LadiesNightJamshedpur from './LadiesNightJamshedpur';

const contentComponents = {
  'top-5-night-clubs-jamshedpur-2026': Top5NightClubs2026,
  'best-dj-nights-jamshedpur-weekly-guide': BestDJNightsWeeklyGuide,
  'ultimate-guide-nightlife-jamshedpur': UltimateGuideNightlife,
  'where-to-party-jamshedpur-saturday-night': WhereToPartySaturday,
  'things-to-do-jamshedpur-at-night': ThingsToDoAtNight,
  'bachelor-party-jamshedpur-guide': BachelorPartyJamshedpur,
  'ladies-night-jamshedpur-guide': LadiesNightJamshedpur,
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getBlogPostBySlug(slug);

  if (!post) {
    navigate('/blog');
    return null;
  }

  const ContentComponent = contentComponents[slug];
  const relatedPosts = blogPosts.filter(p => p.id !== post.id).slice(0, 3);

  const breadcrumbItems = [
    { name: 'Blog', url: '/blog' },
    { name: post.title, url: null }
  ];

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <SEOHead
        title={`${post.title} | Scratch Unleashed Blog`}
        description={post.excerpt}
        keywords={post.keywords}
        canonical={`https://scratchjsr.com/blog/${post.slug}`}
        image={post.image || 'https://scratchjsr.com/images/gallery-1.jpg'}
        type="article"
        publishedTime={post.publishDate}
        modifiedTime={post.modifiedDate || post.publishDate}
      />
      <ArticleSchema post={post} />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://scratchjsr.com/' },
        { name: 'Blog', url: 'https://scratchjsr.com/blog' },
        { name: post.title, url: `https://scratchjsr.com/blog/${post.slug}` }
      ]} />

      <div className="h-16"></div>

      <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Article Header */}
        <article>
          <header className="mb-8">
            <h1 className="font-bebas text-4xl sm:text-5xl lg:text-6xl mb-4">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-[var(--text-muted)] mb-6">
              <div className="flex items-center gap-2">
                <User size={16} />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                <span>{new Date(post.publishDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={16} />
                <span>{post.readTime}</span>
              </div>
            </div>
            {post.image && (
              <div 
                className="w-full h-64 sm:h-96 bg-cover bg-center rounded-2xl mb-8"
                style={{ backgroundImage: `url(${post.image})` }}
              ></div>
            )}
          </header>

          {/* Article Content */}
          <div className="prose prose-invert max-w-none">
            {ContentComponent ? <ContentComponent /> : <p>Content not available</p>}
          </div>
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-16 pt-12 border-t border-[var(--border)]">
            <h2 className="font-bebas text-3xl mb-6">Related Nightlife Guides</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Card 
                  key={relatedPost.id}
                  className="overflow-hidden bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl cursor-pointer hover:border-[var(--accent-gold)] transition-all duration-300"
                  onClick={() => navigate(`/blog/${relatedPost.slug}`)}
                >
                  <div 
                    className="h-40 bg-cover bg-center"
                    style={{ backgroundImage: `url(${relatedPost.image})` }}
                  ></div>
                  <div className="p-4">
                    <h3 className="font-semibold text-sm mb-2 line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-xs text-[var(--text-muted)]">
                      {relatedPost.readTime}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl p-8 text-center">
          <h2 className="font-bebas text-3xl mb-4">Ready to Experience It Yourself?</h2>
          <p className="text-[var(--text-dim)] mb-6 max-w-2xl mx-auto">
            Book your table at Scratch Unleashed and discover why we're Jamshedpur's premier nightlife destination.
          </p>
          <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
            Reserve Your Table
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
